import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, DollarSign, Package, Users, Store, Loader2, Plus, ClipboardList, CheckCircle2, Tag, UserCog, Upload, Trash2, XCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";

const STAGE_LABEL = {
  PLACED: "Pesanan Dibuat", PURCHASED: "Dibeli dari Supplier", GUANGZHOU_HUB: "Gudang Guangzhou",
  CUSTOMS: "Bea Cukai", INDO_WAREHOUSE: "Gudang Indonesia", DISPATCHED_KEDAI: "Dikirim ke Kedai",
  READY_PICKUP: "Siap Diambil", COMPLETED: "Selesai",
};
const STAGES = Object.keys(STAGE_LABEL);

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [rate, setRate] = useState(null);
  const [bank, setBank] = useState(null);
  const [orders, setOrders] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [markings, setMarkings] = useState([]);
  const [report, setReport] = useState(null);
  const [markForm, setMarkForm] = useState({ product_type: "", hscode: "", qty: 1, photo_path: "", notes: "", order_id: "" });
  const [markUploading, setMarkUploading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({});
  const [newKiosk, setNewKiosk] = useState({ name: "", region: "", subdistrict: "", city: "", address: "", phone: "", agent_email: "" });
  const [savingRate, setSavingRate] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/settings/rate").then((r) => setRate(r.data));
    api.get("/settings/bank").then((r) => setBank(r.data));
    api.get("/orders").then((r) => setOrders(r.data.orders));
    api.get("/kiosks", { params: { all: true } }).then((r) => setKiosks(r.data.kiosks));
    api.get("/requests").then((r) => setRequests(r.data.requests));
    api.get("/admin/members").then((r) => setMembers(r.data.members));
    api.get("/markings").then((r) => setMarkings(r.data.markings)).catch(() => {});
    api.get("/admin/kiosk-report").then((r) => setReport(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const onMarkFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setMarkUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/uploads", fd); setMarkForm((x) => ({ ...x, photo_path: data.path })); toast.success("Foto terunggah"); }
    catch (err) { toast.error(apiError(err)); } finally { setMarkUploading(false); }
  };

  const createMarking = async (e) => {
    e.preventDefault();
    if (!markForm.product_type.trim()) return toast.error("Isi jenis barang");
    try {
      await api.post("/markings", { ...markForm, qty: parseInt(markForm.qty) || 1 });
      toast.success("Pengajuan marking terkirim ke gudang China");
      setMarkForm({ product_type: "", hscode: "", qty: 1, photo_path: "", notes: "", order_id: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const decideMember = async (id, action) => {
    try { await api.patch(`/membership/${id}/${action}`); toast.success(action === "approve" ? "Member disetujui" : "Pengajuan ditolak"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const sendQuote = async (id, reqQty) => {
    const f = quoteForm[id] || {};
    if (!f.verified_price_cny) { toast.error("Isi harga asli (¥) hasil kroscek"); return; }
    try {
      await api.patch(`/requests/${id}/quote`, {
        verified_price_cny: parseFloat(f.verified_price_cny),
        qty: parseInt(f.qty) || reqQty,
        shipping_estimate_idr: parseInt(f.shipping_estimate_idr) || 0,
        admin_note: f.admin_note || "",
      });
      toast.success("Penawaran terkirim ke konsumen");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveRate = async () => {
    setSavingRate(true);
    try { await api.put("/settings/rate", { base_rate: parseFloat(rate.base_rate), markup_per_cny: parseFloat(rate.markup_per_cny), usd_to_idr: parseFloat(rate.usd_to_idr), usd_markup_pct: parseFloat(rate.usd_markup_pct) }); toast.success("Kurs diperbarui"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingRate(false); }
  };

  const saveBank = async () => {
    if (!bank.bank_name || !bank.bank_account || !bank.bank_owner) { toast.error("Lengkapi semua data rekening"); return; }
    setSavingBank(true);
    try { await api.put("/settings/bank", { bank_name: bank.bank_name, bank_account: bank.bank_account, bank_owner: bank.bank_owner }); toast.success("Rekening diperbarui"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingBank(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); toast.success("Status pesanan diperbarui"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cancelOrder = async (id) => {
    if (!window.confirm("Batalkan pengiriman pesanan ini? Status akan menjadi Dibatalkan.")) return;
    const reason = window.prompt("Alasan pembatalan (opsional, akan terlihat oleh pelanggan):", "") || "";
    try { await api.post(`/orders/${id}/cancel`, { reason }); toast.success("Pengiriman dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteMarking = async (id) => {
    if (!window.confirm("Hapus pengajuan marking ini? Tindakan tidak bisa dibatalkan.")) return;
    try { await api.delete(`/markings/${id}`); toast.success("Pengajuan marking dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Hapus riwayat pengiriman ini secara PERMANEN? Cocok untuk data testing. Tindakan tidak bisa dibatalkan.")) return;
    try { await api.delete(`/orders/${id}`); toast.success("Riwayat pengiriman dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addKiosk = async (e) => {
    e.preventDefault();
    try { await api.post("/kiosks", newKiosk); toast.success("Kedai ditambahkan"); setNewKiosk({ name: "", region: "", subdistrict: "", city: "", address: "", phone: "", agent_email: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!stats || !rate || !bank) return <div className="py-24 flex justify-center text-emerald-600"><Loader2 className="animate-spin" size={32} /></div>;
  const eff = parseFloat(rate.base_rate) + parseFloat(rate.markup_per_cny);
  const effUsd = parseFloat(rate.usd_to_idr) * (1 + parseFloat(rate.usd_markup_pct) / 100);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3"><LayoutDashboard className="text-secondary" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">Dashboard {user?.role === "owner" ? "Owner" : "Admin"}</h1></div>
        {user?.role === "owner" && <Link to="/kelola-akun" data-testid="admin-kelola-akun" className="inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-stone-800"><UserCog size={16} />Kelola Akun</Link>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: DollarSign, label: "Total Omzet", val: rupiah(stats.revenue_idr) },
          { icon: Package, label: "Total Pesanan", val: stats.total_orders },
          { icon: Users, label: "Pelanggan", val: stats.total_users },
          { icon: Store, label: "Kedai Mitra", val: stats.total_kiosks },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-200 p-5">
            <s.icon className="text-emerald-600 mb-2" size={22} />
            <p className="text-xl font-display font-bold text-stone-900">{s.val}</p>
            <p className="text-sm text-stone-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Rate editor */}
      {user?.role === "owner" && (<>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-8">
        <h2 className="font-display font-semibold text-lg mb-1">Pengaturan Harga & Kurs</h2>
        <p className="text-xs text-stone-500 mb-4">Semua harga ditampilkan ke konsumen sudah dalam Rupiah dengan rumus di bawah.</p>
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Kurs Yuan (CNY → Rupiah)</p>
        <div className="grid sm:grid-cols-3 gap-4 items-end mb-5">
          <div><label className="text-sm font-medium text-stone-600">Kurs Dasar (¥1 = Rp)</label>
            <input data-testid="rate-base" type="number" value={rate.base_rate} onChange={(e) => setRate({ ...rate, base_rate: e.target.value })} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <div><label className="text-sm font-medium text-stone-600">Markup per ¥ (Rp)</label>
            <input data-testid="rate-markup" type="number" value={rate.markup_per_cny} onChange={(e) => setRate({ ...rate, markup_per_cny: e.target.value })} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2.5 font-mono">¥1 = Rp{Math.round(eff).toLocaleString("id-ID")}</p>
        </div>
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Kurs USD (USD → Rupiah, +markup %)</p>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div><label className="text-sm font-medium text-stone-600">Kurs USD ($1 = Rp)</label>
            <input data-testid="rate-usd" type="number" value={rate.usd_to_idr} onChange={(e) => setRate({ ...rate, usd_to_idr: e.target.value })} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <div><label className="text-sm font-medium text-stone-600">Markup USD (%)</label>
            <input data-testid="rate-usd-markup" type="number" value={rate.usd_markup_pct} onChange={(e) => setRate({ ...rate, usd_markup_pct: e.target.value })} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2.5 font-mono">$1 = Rp{Math.round(effUsd).toLocaleString("id-ID")} (+{rate.usd_markup_pct}%)</p>
        </div>
        <button data-testid="rate-save" onClick={saveRate} disabled={savingRate} className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-6 rounded-lg inline-flex items-center gap-2 disabled:opacity-60">
          {savingRate && <Loader2 className="animate-spin" size={16} />}Simpan Semua Kurs</button>
      </div>

      {/* Bank editor */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-8" data-testid="admin-bank-editor">
        <h2 className="font-display font-semibold text-lg mb-1">Rekening Penerima Pembayaran Member</h2>
        <p className="text-xs text-stone-500 mb-4">Rekening ini otomatis tampil di halaman Member untuk transfer pembayaran paket.</p>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div><label className="text-sm font-medium text-stone-600">Nama Bank</label>
            <input data-testid="bank-name" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="BCA" className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <div><label className="text-sm font-medium text-stone-600">No. Rekening</label>
            <input data-testid="bank-account" value={bank.bank_account} onChange={(e) => setBank({ ...bank, bank_account: e.target.value })} placeholder="1234567890" className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
          <div><label className="text-sm font-medium text-stone-600">Atas Nama</label>
            <input data-testid="bank-owner" value={bank.bank_owner} onChange={(e) => setBank({ ...bank, bank_owner: e.target.value })} placeholder="Nama pemilik rekening" className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" /></div>
        </div>
        <button data-testid="bank-save" onClick={saveBank} disabled={savingBank} className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-6 rounded-lg inline-flex items-center gap-2 disabled:opacity-60">
          {savingBank && <Loader2 className="animate-spin" size={16} />}Simpan Rekening</button>
      </div>
      </>)}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[["orders", "Pesanan"], ["requests", "Permintaan"], ["members", "Member"], ["marking", "Marking China"], ["kiosks", "Kedai Mitra"], ...(user?.role === "owner" ? [["report", "Laporan Kedai"]] : [])].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-stone-400 text-sm">Belum ada pesanan.</p>}
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="font-mono text-xs text-stone-400">{o.awb}</p>
                <p className="text-sm font-medium text-stone-800">{o.user_name} · {rupiah(o.total_idr)} · {o.fulfillment === "kedai" ? "Kedai" : "Kirim"}</p>
              </div>
              {o.status === "CANCELLED" ? (
                <span data-testid={`admin-cancelled-${o.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-100 text-red-700">Dibatalkan</span>
              ) : (
                <div className="flex items-center gap-2">
                  <select data-testid={`admin-status-${o.id}`} value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400">
                    {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                  </select>
                  <button data-testid={`admin-cancel-${o.id}`} onClick={() => cancelOrder(o.id)} title="Batalkan pengiriman" className="inline-flex items-center gap-1 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-3 py-2 rounded-lg"><XCircle size={15} />Batalkan</button>
                </div>
              )}
              <button data-testid={`admin-order-delete-${o.id}`} onClick={() => deleteOrder(o.id)} title="Hapus riwayat pengiriman (permanen)" className="text-stone-300 hover:text-red-600 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "kiosks" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-kiosks">
          <div className="space-y-3">
            {kiosks.map((k) => (
              <div key={k.id} className="bg-white rounded-2xl border border-stone-200 p-4">
                <p className="font-semibold text-stone-800 text-sm">{k.name}</p>
                <p className="text-xs text-stone-500">{k.address}, {k.subdistrict}, {k.city} ({k.region})</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-stone-500"><span>{k.phone}</span>{k.agent_email && <span>Agen: {k.agent_email}</span>}<span className="text-amber-600 font-semibold">{k.points} poin</span></div>
              </div>
            ))}
          </div>
          <form onSubmit={addKiosk} className="bg-white rounded-2xl border border-stone-200 p-5 h-fit">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Plus size={18} />Tambah Kedai Mitra</h3>
            <div className="space-y-2.5">
              {[["name", "Nama Kedai"], ["region", "Provinsi"], ["city", "Kota/Kabupaten"], ["subdistrict", "Kecamatan"], ["address", "Alamat"], ["phone", "No. HP"], ["agent_email", "Email Agen (opsional)"]].map(([k, ph]) => (
                <input key={k} data-testid={`kiosk-form-${k}`} value={newKiosk[k]} onChange={(e) => setNewKiosk({ ...newKiosk, [k]: e.target.value })} required={["name", "region", "city", "subdistrict", "address"].includes(k)}
                  placeholder={ph} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              ))}
              <button data-testid="kiosk-form-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg">Tambah Kedai</button>
              <p className="text-xs text-stone-400">Jika email agen diisi & sudah terdaftar, akun tsb otomatis jadi agen kedai ini.</p>
            </div>
          </form>
        </div>
      )}

      {tab === "requests" && (
        <div className="space-y-3" data-testid="admin-requests">
          {requests.length === 0 && <p className="text-stone-400 text-sm">Belum ada permintaan cek harga.</p>}
          {requests.map((r) => {
            const eff = rate ? parseFloat(rate.base_rate) + parseFloat(rate.markup_per_cny) : 0;
            const f = quoteForm[r.id] || {};
            const cny = parseFloat(f.verified_price_cny) || 0;
            const qty = parseInt(f.qty) || r.qty;
            const ship = parseInt(f.shipping_estimate_idr) || 0;
            const unitIdr = Math.round(cny * eff);
            const previewTotal = unitIdr * qty + ship;
            const setQF = (k, v) => setQuoteForm((s) => ({ ...s, [r.id]: { ...s[r.id], [k]: v } }));
            return (
              <div key={r.id} data-testid={`admin-req-${r.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
                <div className="flex items-start gap-3">
                  {r.photo_path ? <AuthImage path={r.photo_path} className="w-14 h-14 rounded-lg object-cover" /> : r.image_url ? <img src={r.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-stone-100 flex items-center justify-center text-stone-300"><ClipboardList size={20} /></div>}
                  <div className="flex-1">
                    <p className="font-medium text-stone-800 text-sm">{r.title}</p>
                    <p className="text-xs text-stone-400">{r.user_name} · {r.qty} pcs {r.notes && `· ${r.notes}`}</p>
                    {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 underline">buka link produk</a>}
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-stone-100 text-stone-600">{r.status}</span>
                </div>

                {r.status === "PENDING" && (
                  <div className="mt-3 border-t border-stone-100 pt-3">
                    <p className="text-xs font-semibold text-stone-500 mb-2">Kroscek & hitung ulang harga asli:</p>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <div><label className="text-[11px] text-stone-500">Harga asli (¥)</label><input data-testid={`quote-cny-${r.id}`} type="number" value={f.verified_price_cny || ""} onChange={(e) => setQF("verified_price_cny", e.target.value)} className="w-full border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-emerald-400" /></div>
                      <div><label className="text-[11px] text-stone-500">Qty</label><input data-testid={`quote-qty-${r.id}`} type="number" value={f.qty ?? r.qty} onChange={(e) => setQF("qty", e.target.value)} className="w-full border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-emerald-400" /></div>
                      <div><label className="text-[11px] text-stone-500">Ongkir (Rp)</label><input data-testid={`quote-ship-${r.id}`} type="number" value={f.shipping_estimate_idr || ""} onChange={(e) => setQF("shipping_estimate_idr", e.target.value)} className="w-full border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-emerald-400" /></div>
                      <div><label className="text-[11px] text-stone-500">Catatan</label><input data-testid={`quote-note-${r.id}`} value={f.admin_note || ""} onChange={(e) => setQF("admin_note", e.target.value)} className="w-full border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-emerald-400" /></div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                      <p className="text-sm text-emerald-700">Preview → satuan <b>{rupiah(unitIdr)}</b> · total <b>{rupiah(previewTotal)}</b> <span className="text-xs text-stone-400">(kurs ¥1=Rp{Math.round(eff).toLocaleString("id-ID")})</span></p>
                      <button data-testid={`quote-send-${r.id}`} onClick={() => sendQuote(r.id, r.qty)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5"><CheckCircle2 size={15} />Kirim Penawaran</button>
                    </div>
                  </div>
                )}
                {r.status !== "PENDING" && r.quote && (
                  <div className="mt-2 text-sm text-stone-600">Ditawarkan: <b>{rupiah(r.quote.total_idr)}</b> ({r.quote.qty} pcs @ {rupiah(r.quote.unit_price_idr)}) · asli ¥{r.quote.verified_price_cny}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {tab === "members" && (
        <div className="space-y-3" data-testid="admin-members">
          {members.length === 0 && <p className="text-stone-400 text-sm">Belum ada pengajuan member.</p>}
          {members.map((mb) => (
            <div key={mb.id} data-testid={`admin-member-${mb.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex items-start gap-3">
              {mb.payment_proof ? <AuthImage path={mb.payment_proof} className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-stone-100" />}
              <div className="flex-1">
                <p className="font-medium text-stone-800 text-sm">{mb.name} <span className="text-stone-400">· {mb.email}</span></p>
                <p className="text-xs text-stone-400">{mb.phone} {mb.note && `· ${mb.note}`}</p>
                <span className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${mb.status === "active" ? "bg-emerald-100 text-emerald-700" : mb.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>{mb.status}</span>
              </div>
              {mb.status === "active" ? (
                <span className="text-emerald-600 text-xs font-semibold whitespace-nowrap">✓ Member</span>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <button data-testid={`member-approve-${mb.id}`} onClick={() => decideMember(mb.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Setujui</button>
                  <button data-testid={`member-reject-${mb.id}`} onClick={() => decideMember(mb.id, "reject")} className="border border-stone-200 text-stone-500 text-xs px-3 py-1.5 rounded-lg">Tolak</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "marking" && (
        <div className="grid lg:grid-cols-5 gap-6" data-testid="admin-marking">
          <form onSubmit={createMarking} className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5 h-fit">
            <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Tag size={18} />Ajukan Marking ke Gudang China</h3>
            <p className="text-xs text-stone-500 mb-3">Pilih pesanan barangnya, lalu ajukan ke gudang Guangzhou. Kode marking yang mereka berikan otomatis jadi <b>nomor lacak</b> pesanan tersebut.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-stone-500">Pesanan / Barang</label>
                <select data-testid="mark-order" value={markForm.order_id} onChange={(e) => setMarkForm({ ...markForm, order_id: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400">
                  <option value="">— Tanpa pesanan (opsional) —</option>
                  {orders.map((o) => <option key={o.id} value={o.id}>{o.awb} · {o.user_name || "—"}</option>)}
                </select>
              </div>
              <input data-testid="mark-type" value={markForm.product_type} onChange={(e) => setMarkForm({ ...markForm, product_type: e.target.value })} required placeholder="Jenis barang (cth: Sepatu kulit pria)" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="mark-hscode" value={markForm.hscode} onChange={(e) => setMarkForm({ ...markForm, hscode: e.target.value })} placeholder="HS Code (opsional)" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                <input data-testid="mark-qty" type="number" min="1" value={markForm.qty} onChange={(e) => setMarkForm({ ...markForm, qty: e.target.value })} placeholder="Qty" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              </div>
              <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-500 cursor-pointer hover:border-emerald-400">
                {markUploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}{markForm.photo_path ? "Ganti foto barang" : "Upload foto barang"}
                <input data-testid="mark-photo" type="file" accept="image/*" onChange={onMarkFile} className="hidden" />
              </label>
              {markForm.photo_path && <AuthImage path={markForm.photo_path} className="w-24 h-24 rounded-lg object-cover" />}
              <textarea data-testid="mark-notes" value={markForm.notes} onChange={(e) => setMarkForm({ ...markForm, notes: e.target.value })} rows={2} placeholder="Catatan (opsional)" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none" />
              <button data-testid="mark-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg">Ajukan ke Gudang China</button>
            </div>
          </form>
          <div className="lg:col-span-3 space-y-3">
            {markings.length === 0 && <p className="text-stone-400 text-sm">Belum ada pengajuan marking.</p>}
            {markings.map((m) => (
              <div key={m.id} data-testid={`mark-card-${m.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex gap-3">
                {m.photo_path ? <AuthImage path={m.photo_path} className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-stone-100" />}
                <div className="flex-1">                  <p className="font-medium text-stone-800 text-sm">{m.product_type} · {m.qty} pcs</p>
                  {m.order_awb && <p className="text-xs text-stone-400">Pesanan: <span className="font-mono">{m.order_awb}</span></p>}
                  {m.hscode && <p className="text-xs text-stone-400">HS: {m.hscode}</p>}
                  <span className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${m.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" : m.status === "APPROVED" ? "bg-sky-100 text-sky-700" : m.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{m.status === "RECEIVED" ? "Barang Sampai Gudang China" : m.status === "APPROVED" ? "Sudah Dapat Kode Marking" : m.status === "REJECTED" ? "Ditolak Gudang" : "Menunggu Gudang China"}</span>
                  {m.status === "REJECTED" && <p className="mt-1 text-xs text-red-600" data-testid={`admin-mark-reject-${m.id}`}>Alasan ditolak: {m.reject_reason || "-"}</p>}
                  {m.received_photo_path && <div className="mt-2"><p className="text-[11px] text-stone-500 mb-1">Foto bukti barang sampai:</p><AuthImage path={m.received_photo_path} className="w-20 h-20 rounded-lg object-cover" /></div>}
                  {m.status !== "PENDING" && m.marking_code && (
                    <div className="mt-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 text-xs">
                      <p><b>Kode Marking:</b> <span className="font-mono" data-testid={`mark-code-${m.id}`}>{m.marking_code}</span></p>
                      <p className="mt-1"><b>Alamat Gudang:</b> {m.warehouse_address}</p>
                      {m.warehouse_note && <p className="mt-1 text-stone-500">Catatan: {m.warehouse_note}</p>}
                      <p className="mt-1 text-emerald-700">→ Berikan kode &amp; alamat ini ke supplier.</p>
                    </div>
                  )}
                </div>
                <button data-testid={`mark-delete-${m.id}`} onClick={() => deleteMarking(m.id)} title="Hapus pengajuan marking" className="self-start text-stone-300 hover:text-red-600 p-1"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "report" && report && (
        <div className="space-y-8" data-testid="admin-report">
          <div>
            <h2 className="font-display font-semibold text-lg mb-3">Ringkasan per Daerah (Induk Kedai)</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {report.regions.length === 0 && <p className="text-stone-400 text-sm">Belum ada data.</p>}
              {report.regions.map((r) => (
                <div key={r.region} data-testid={`report-region-${r.region}`} className="bg-white rounded-2xl border border-stone-200 p-5">
                  <p className="font-display font-bold text-stone-900">{r.region}</p>
                  <p className="text-xs text-stone-500 mb-3">{r.kiosks} kedai mitra</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xl font-display font-bold text-emerald-700">{r.total}</p><p className="text-[11px] text-stone-500">Paket</p></div>
                    <div><p className="text-xl font-display font-bold text-sky-700">{r.completed}</p><p className="text-[11px] text-stone-500">Selesai</p></div>
                    <div><p className="text-xl font-display font-bold text-amber-600">{r.points}</p><p className="text-[11px] text-stone-500">Poin</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display font-semibold text-lg mb-3">Performa per Kedai</h2>
            <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-stone-500 border-b border-stone-100">
                  <th className="p-3 font-medium">Kedai</th><th className="p-3 font-medium">Daerah</th>
                  <th className="p-3 font-medium text-center">Paket</th><th className="p-3 font-medium text-center">Proses</th>
                  <th className="p-3 font-medium text-center">Selesai</th><th className="p-3 font-medium text-center">Poin</th>
                  <th className="p-3 font-medium text-center">Rata2 Total</th><th className="p-3 font-medium text-center">Antar ke Kedai</th>
                </tr></thead>
                <tbody>
                  {report.kiosks.map((k) => (
                    <tr key={k.id} data-testid={`report-kiosk-${k.id}`} className="border-b border-stone-50 last:border-0">
                      <td className="p-3 font-medium text-stone-800">{k.name}</td>
                      <td className="p-3 text-stone-500">{k.city}, {k.region}</td>
                      <td className="p-3 text-center">{k.total}</td>
                      <td className="p-3 text-center text-amber-600">{k.in_transit}</td>
                      <td className="p-3 text-center text-emerald-700 font-semibold">{k.completed}</td>
                      <td className="p-3 text-center">{k.points}</td>
                      <td className="p-3 text-center">{k.avg_lead_days != null ? `${k.avg_lead_days} hari` : "—"}</td>
                      <td className="p-3 text-center">{k.avg_lastmile_days != null ? `${k.avg_lastmile_days} hari` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
